import classNames from 'clsx';
import { noop } from 'es-toolkit';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { CIRCLE } from 'src/common/constants/tile';
import commonCss from '../../../../styles/common.module.scss';
import assetUrl from '../../../../utils/assetUrl';
import { isDiscordMode } from '../../../../utils/discord';
import ClampText from '../../../shared/ClampText';
import ImageWithDefault from '../../../shared/ImageWithDefault';
import { GuideItemLongPressMenu } from '../../shared/GuideItemLongPressMenu';
import GuideItemTitle from '../../shared/GuideItemTitle';
import css from '../../shared/guide-item.module.scss';
import WithGuideItemActions from '../../shared/WithGuideItemActions';
import tileCss from './tile.module.scss';

const Tile = ({ guideItem, index, onClick = noop, isMatrix, tileWidthAsNumber }) => {
  const [isSelected, setIsSelected] = useState(guideItem?.isSelectedInterest || false);
  const isSelectableItem = guideItem.isSelectable;
  const { tileSize, tileTitle, tileShape, tileTitleAlignment } = guideItem.styles || {};

  const squareTileOverlayClassNames = classNames({
    [tileCss.selectableOverlay]: isSelectableItem,
    [commonCss.overlay]: !isSelectableItem,
    [tileCss.matrixOverlay]: isMatrix,
  });

  const dotClassNames = classNames(css.dot, {
    [css[`dot${tileSize}`]]: tileSize,
  });

  const dogEarClassNames = classNames(css.dogEar, {
    [css[`dogEar${tileSize}`]]: tileSize,
  });

  const captionContainerClassNames = classNames(tileCss.captionContainer, {
    [tileCss[`textAlign${tileTitleAlignment}`]]: tileTitleAlignment,
  });

  const clampTextClassNames = classNames(css.guideItemTitleMultiLine, {
    [tileCss.selectableGuideItemTitle]: isSelectableItem,
  });
  const badgeImageClassNames = classNames(css.badgeImage, {
    [css[`badgeImage${tileSize}`]]: tileSize,
  });

  const applyBadge = (url, alt) => (
    <img className={badgeImageClassNames} src={assetUrl(url)} alt={alt} />
  );

  function handleClick() {
    onClick(guideItem.guideId);

    if (isSelectableItem) {
      setIsSelected((_isSelected) => !_isSelected);
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick();
    }
  }

  return (
    <GuideItemLongPressMenu guideItem={guideItem}>
      <div
        data-testid={`squareTileOverlay guideItem-${index} guideItemImageWrapper`}
        data-testtype="squareTile"
        className={classNames(`${squareTileOverlayClassNames}`, {
          [tileCss.tileContainer]: !isMatrix,
        })}
        onClick={handleClick}
        onKeyDown={handleKeyDown} // Add keydown event listener
        /* biome-ignore lint/a11y/useSemanticElements: TODO: explore changing div to button */
        role="button" // Add role attribute
        tabIndex={0} // Add tabIndex attribute
      >
        <WithGuideItemActions
          guideItem={guideItem}
          tileSize={tileSize}
          tileShape={tileShape}
          isSelected={isSelected}
          playButtonStyles={
            isMatrix
              ? {
                  height: 'auto',
                }
              : {}
          }
          style={
            tileShape !== CIRCLE && tileWidthAsNumber < 9
              ? {
                  borderRadius: '12px',
                }
              : {}
          }
        >
          {guideItem?.isNew && (
            <div data-testid="newPlayableItemContainer" className={css.newPlayableItemContainer}>
              <div data-testid="newPlayableItemIndicator" className={dotClassNames} />
              <div className={dogEarClassNames} />
            </div>
          )}
          {guideItem?.isPremium &&
            guideItem?.isAudiobook &&
            applyBadge('assets/img/shared/premium-badge.svg', 'Premium Station Icon')}
          {/* The Discord check can be removed when PLATFORM-17505 is resolved */}
          {!guideItem?.isPremium &&
            guideItem?.isBoost &&
            !isDiscordMode() &&
            applyBadge('assets/img/shared/boost-badge.svg', 'Boost Station Icon')}
          <ImageWithDefault
            dataTestId="guideItemImage"
            src={guideItem.image}
            alt={guideItem?.a11yTitle || guideItem?.title}
            className={tileCss.image}
          />
        </WithGuideItemActions>
        {guideItem.title && tileTitle !== false && (
          <GuideItemTitle
            dataTestId="guideItemTitleLink guideItemTitle"
            guideItem={guideItem}
            hasOwnTitleWrapper
            hasImage
            className={captionContainerClassNames}
          >
            <ClampText clamp={2} className={clampTextClassNames}>
              {guideItem.title}
            </ClampText>
          </GuideItemTitle>
        )}
      </div>
    </GuideItemLongPressMenu>
  );
};

Tile.propTypes = {
  guideItem: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onClick: PropTypes.func,
  isMatrix: PropTypes.bool,
  tileWidthAsNumber: PropTypes.number,
};

export default Tile;
